import Head from 'next/head';
import React, { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { getRentals, goToRentals } from '@/actions/rentals';
import PopularPostsList from '@/components/blog/PopularPostsList';
import MaxWidth from '@/components/common/layout/MaxWidth';
import NarrowWidth from '@/components/common/layout/NarrowWidth';
import { RentalsInlineList } from '@/components/common/RentalsList/RentalsInlineList';
import BlogPostHero from '@/components/route/blog/BlogPostHero/BlogPostHero';
import BlogStaysBanner from '@/components/route/blog/BlogStaysBanner';
import CategoryPosts from '@/components/route/blog/CategoryPosts';
import FeaturedPosts from '@/components/route/blog/FeaturedPosts';
import RentalRigs from '@/components/route/blog/RentalRigs';
import TitleLink from '@/components/route/blog/TitleLink';
import Heading from '@/components/switchback/Heading/Heading';
import NewsLetterSignup from '@/components/switchback/NewsLetterSignup/NewsLetterSignup';
import { EListingSource } from '@/services/analytics/listings/types';
import { getCategoryPosts, SuccessPostResponse } from '@/services/getBlogCategoryPosts';
import getBlogRecentPosts, { FeaturedPostsIDs } from '@/services/getBlogRecentPosts';
import { trackEvent } from '@/services/track-event';
import { IBlogPost } from '@/services/types/blog/post.types';
import { deleteUndefined } from '@/utility/deleteUndefined';
import hash from '@/utility/hash';
import { getLocale } from '@/utility/i18n';
import { logger } from '@/utility/logger';

import { BlogMenu } from '../components/route/blog/BlogMenu';

export async function getServerSideProps() {
  let results = {};
  try {
    results = await hash({
      recentPosts: getBlogRecentPosts({
        limit: 4,
        featuredId: FeaturedPostsIDs.featured,
      }),
      rvRenters: getCategoryPosts('rv-renters'),
      rvOwners: getCategoryPosts('rv-owners'),
      rvGuides: getCategoryPosts('rv-parks-campgrounds', 3),
      topPosts: getCategoryPosts('top-blogs', 30),
    });
    if (process.env.NODE_ENV !== 'production') {
      deleteUndefined(results);
    }
  } catch (error) {
    logger.captureExceptionWithDatadog(error, { url: '/blog' });
    throw error;
  }
  return { props: results };
}

interface IBlogIndexProps {
  recentPosts: IBlogPost[];
  rvRenters: SuccessPostResponse;
  rvOwners: SuccessPostResponse;
  rvGuides: SuccessPostResponse;
  topPosts: SuccessPostResponse;
}

const BlogIndex: React.FC<IBlogIndexProps> = ({
  recentPosts,
  rvRenters,
  rvOwners,
  rvGuides,
  topPosts,
}) => {
  const locale = getLocale();
  const intl = useIntl();
  const newsletterTitle = intl.formatMessage({
    defaultMessage: 'Be the first to get doses of destination inspiration, and discount codes.',
    id: 'RdFytN',
  });
  const pageType = 'Blog page';
  const dispatch = useDispatch();
  const getPageRentals = useCallback((filters: any) => dispatch(getRentals(filters)), [dispatch]);

  useEffect(() => {
    getPageRentals({ count: 5 });
    trackEvent({
      event: 'Blog : Viewed page',
      pageType: 'Blog page',
    });
  }, [getPageRentals]);

  return (
    <div className="bg-canvas-100">
      <Head>
        <title>RV Stories, Motorhomes, Camper Rentals & Airstreams | Outdoorsy.com</title>
        <link rel="canonical" href="https://www.outdoorsy.com/blog" />

        <meta
          name="description"
          content="Outdoorsy's unique blog for RV travel and RV rental. Useful guides for Class A, B and C motorhomes, incredible campervan stories, and lots of cool RVs."
        />

        <meta property="fb:app_id" content="604465049688341" />
        <meta name="msvalidate.01" content="1DD3784B3E7F8CD7B4C595940717F938" />
        <meta name="apple-itunes-app" content="app-id=1443835927, affiliate-data=1010lvba" />
        <meta
          name="google-site-verification"
          content="DuUf3p3Pr3-gmZfpMIg3gyG75gl7C3JpXWb0-ilQJbI"
        />

        <link rel="dns-prefetch" href="//s.w.org" />

        {intl.locale === 'en-us' ? (
          <meta name="robots" content="index, follow, max-image-preview:large" />
        ) : (
          <meta name="robots" content="noindex" />
        )}

        <meta
          name="googlebot"
          content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
        />
        <meta
          name="bingbot"
          content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
        />

        <meta property="og:locale" content="en_US" />
        <meta property="og:type" content="article" />
        <meta
          property="og:title"
          content="RV Stories, Motorhomes, Camper Rentals & Airstreams | Outdoorsy.com"
        />
        <meta property="og:url" content="https://www.outdoorsy.com/blog" />
        <meta property="og:site_name" content="Outdoorsy RV Blogs" />
        <meta
          property="og:description"
          content="Outdoorsy's unique blog for RV travel and RV rental. Useful guides for Class A, B and C motorhomes, incredible campervan stories, and lots of cool RVs."
        />
        <meta name="twitter:card" content="summary" />
      </Head>

      {recentPosts[0] && <BlogPostHero post={recentPosts[0]} />}

      <div className="relative z-50 mx-auto">
        <BlogMenu />
      </div>

      <hr />

      {locale === 'en-us' && <BlogStaysBanner />}

      <MaxWidth className="m-auto mt-12">
        <Heading level={1} className="mb-8 highlight autoType800">
          {intl.formatMessage({ defaultMessage: 'New and noteworthy.', id: '2SuepO' })}
        </Heading>
        <FeaturedPosts posts={recentPosts.slice(1, 4)} />
        <hr />
      </MaxWidth>

      <NarrowWidth className="mt-12 lg:max-w-lg" collapseMobile>
        <TitleLink
          slug="for-renters"
          title={intl.formatMessage({
            defaultMessage: 'Top articles for beginners.',
            id: 'GNOH5Y',
          })}
        />
        <CategoryPosts posts={rvRenters.posts} inline />
      </NarrowWidth>
      <NarrowWidth className="mt-12 lg:max-w-lg" collapseMobile>
        <TitleLink
          slug="for-owners"
          title={intl.formatMessage({
            defaultMessage: 'Top articles for RV Owners.',
            id: 'WCt2NH',
          })}
        />
        <CategoryPosts posts={rvOwners.posts} inline />
      </NarrowWidth>

      <MaxWidth className="m-auto mt-12">
        <hr />
        <Heading className="my-8 highlight autoType800">
          {intl.formatMessage({ defaultMessage: 'Guides and resources.', id: '+4SHVG' })}
        </Heading>
        <FeaturedPosts posts={rvGuides.posts} />
      </MaxWidth>

      <div className="py-4 bg-white pb-14">
        <MaxWidth className="m-auto mt-12 ">
          <Heading className="mx-4 my-8 highlight autoType800 lg:mx-0">
            {intl.formatMessage({ defaultMessage: 'Ready to get started.', id: 'EMpVYE' })}
          </Heading>
          <RentalsInlineList
            goToRentals={() => dispatch(goToRentals())}
            eventSource={EListingSource.GRID_LAYOUT}
          />
        </MaxWidth>
      </div>

      <MaxWidth collapseMobile className="m-auto my-12">
        <Heading className="mx-4 my-8 lg:mx-0 highlight autoType800">
          {intl.formatMessage({ defaultMessage: 'Travel the way you want it.', id: '7ndkZ9' })}
        </Heading>
        <RentalRigs />
      </MaxWidth>

      <div className="py-2 mt-4 bg-white">
        <MaxWidth className="m-auto my-12">
          <NewsLetterSignup title={newsletterTitle} source={pageType} />
        </MaxWidth>
      </div>

      <PopularPostsList topPosts={topPosts} />
    </div>
  );
};

export default BlogIndex;
